import { STORAGE_THRESHOLDS } from "@bafe/config";
import type { CountPolicy, SubscriptionStatus } from "@bafe/types";

/**
 * Commercial entitlement rules.
 *
 * Bafe sells a storage pool. Mailboxes, domains and aliases are resources
 * inside that pool, never billable seats. Three rules follow from that and are
 * enforced here:
 *
 *   1. Aggregate consumption across the subscription is the storage limit.
 *   2. Configured mailbox quotas are ceilings; their sum may exceed the pool
 *      and must never be treated as purchased capacity.
 *   3. Resource counts are limited only by operational safety ceilings.
 */

export interface PlanEntitlement {
	totalStorageBytes: number;
	mailboxCountPolicy: CountPolicy;
	domainCountPolicy: CountPolicy;
	mailboxCountCeiling: number | null;
	domainCountCeiling: number | null;
	hourlySendLimitPerMailbox: number;
	dailySendLimitPerMailbox: number | null;
}

export interface AccountUsage {
	/** Sum of actual bytes stored across every mailbox in the subscription. */
	usedBytes: number;
	mailboxCount: number;
	domainCount: number;
}

export type StorageLevel = "ok" | "informational" | "warning" | "critical";

export interface StorageState {
	usedBytes: number;
	totalBytes: number;
	remainingBytes: number;
	fraction: number;
	level: StorageLevel;
	/** Customer-facing summary, e.g. "17.5 GB of 25 GB used". */
	summary: string;
}

export type DenialReason =
	| "storage_exhausted"
	| "subscription_inactive"
	| "operational_ceiling"
	| "quota_below_usage"
	| "quota_invalid";

export interface EntitlementDecision {
	allowed: boolean;
	reason?: DenialReason;
	/** Plain-language explanation, safe to render directly. */
	message?: string;
}

const ALLOWED: EntitlementDecision = { allowed: true };

/** Subscription states in which customers may still create and use resources. */
const OPERATIONAL_STATUSES: readonly SubscriptionStatus[] = [
	"trialing",
	"active",
	"past_due",
	"grace_period",
];

export function computeStorageState(usedBytes: number, totalBytes: number): StorageState {
	const safeTotal = Math.max(totalBytes, 0);
	const fraction = safeTotal === 0 ? 1 : usedBytes / safeTotal;

	let level: StorageLevel = "ok";
	if (fraction >= STORAGE_THRESHOLDS.critical) level = "critical";
	else if (fraction >= STORAGE_THRESHOLDS.warning) level = "warning";
	else if (fraction >= STORAGE_THRESHOLDS.informational) level = "informational";

	return {
		usedBytes,
		totalBytes: safeTotal,
		remainingBytes: Math.max(safeTotal - usedBytes, 0),
		fraction,
		level,
		summary: `${formatBytes(usedBytes)} of ${formatBytes(safeTotal)} used`,
	};
}

export function canCreateMailbox(
	plan: PlanEntitlement,
	usage: AccountUsage,
	subscriptionStatus: SubscriptionStatus,
): EntitlementDecision {
	if (!OPERATIONAL_STATUSES.includes(subscriptionStatus)) {
		return {
			allowed: false,
			reason: "subscription_inactive",
			message: "Reactivate your plan before adding email accounts.",
		};
	}

	// The pool being full blocks new mailboxes; the mailbox count never does.
	if (usage.usedBytes >= plan.totalStorageBytes) {
		return {
			allowed: false,
			reason: "storage_exhausted",
			message: `Your account has reached its ${formatBytes(plan.totalStorageBytes)} storage limit. Increase storage or free some space before adding more mail.`,
		};
	}

	if (plan.mailboxCountPolicy === "capped" || plan.mailboxCountCeiling !== null) {
		const ceiling = plan.mailboxCountCeiling;
		if (ceiling !== null && usage.mailboxCount >= ceiling) {
			return {
				allowed: false,
				reason: "operational_ceiling",
				message:
					"This account has reached a safety limit on the number of email accounts. Contact support and we can raise it.",
			};
		}
	}

	return ALLOWED;
}

export function canCreateDomain(
	plan: PlanEntitlement,
	usage: AccountUsage,
	subscriptionStatus: SubscriptionStatus,
): EntitlementDecision {
	if (!OPERATIONAL_STATUSES.includes(subscriptionStatus)) {
		return {
			allowed: false,
			reason: "subscription_inactive",
			message: "Reactivate your plan before adding domains.",
		};
	}

	if (plan.domainCountPolicy === "capped" || plan.domainCountCeiling !== null) {
		const ceiling = plan.domainCountCeiling;
		if (ceiling !== null && usage.domainCount >= ceiling) {
			return {
				allowed: false,
				reason: "operational_ceiling",
				message:
					"This account has reached a safety limit on the number of domains. Contact support and we can raise it.",
			};
		}
	}

	return ALLOWED;
}

/**
 * Validates an administrative mailbox ceiling.
 *
 * Over-allocation is permitted by design: three 20 GB quotas on a 25 GB plan
 * is a valid configuration because the pool, not the sum of quotas, is the
 * real limit. The only rejections are nonsensical values and shrinking a quota
 * below what the mailbox already holds.
 */
export function validateMailboxQuota(
	quotaBytes: number | null,
	currentUsedBytes: number,
): EntitlementDecision {
	if (quotaBytes === null) return ALLOWED;

	if (!Number.isFinite(quotaBytes) || quotaBytes <= 0) {
		return {
			allowed: false,
			reason: "quota_invalid",
			message: "Enter a storage limit greater than zero, or leave it blank for no limit.",
		};
	}
	if (quotaBytes < currentUsedBytes) {
		return {
			allowed: false,
			reason: "quota_below_usage",
			message: `This mailbox already holds ${formatBytes(currentUsedBytes)}. Set a limit above that, or leave it blank.`,
		};
	}
	return ALLOWED;
}

/**
 * The amount a mailbox may still receive: the smaller of its own remaining
 * quota and the subscription's remaining pool. A mailbox with no quota is
 * bound only by the pool.
 */
export function mailboxHeadroomBytes(
	mailboxUsedBytes: number,
	mailboxQuotaBytes: number | null,
	accountRemainingBytes: number,
): number {
	if (mailboxQuotaBytes === null) return Math.max(accountRemainingBytes, 0);
	const quotaHeadroom = Math.max(mailboxQuotaBytes - mailboxUsedBytes, 0);
	return Math.min(quotaHeadroom, Math.max(accountRemainingBytes, 0));
}

/**
 * Purchased capacity is the plan's storage pool.
 *
 * Present so the intent is greppable and testable: nothing may derive billed
 * capacity by summing mailbox quotas.
 */
export function purchasedStorageBytes(plan: PlanEntitlement): number {
	return plan.totalStorageBytes;
}

/**
 * Team photos and videos consume the same purchased pool as mail.
 * Mailbox quotas are not consulted here.
 */
export function canStoreAdditionalBytes(
	plan: PlanEntitlement,
	usedBytes: number,
	extraBytes: number,
	subscriptionStatus: SubscriptionStatus,
): EntitlementDecision {
	if (!OPERATIONAL_STATUSES.includes(subscriptionStatus)) {
		return {
			allowed: false,
			reason: "subscription_inactive",
			message: "Reactivate your plan before sending this.",
		};
	}
	if (usedBytes + extraBytes > plan.totalStorageBytes) {
		return {
			allowed: false,
			reason: "storage_exhausted",
			message: `Your account has reached its ${formatBytes(plan.totalStorageBytes)} storage limit. Increase storage or free some space before sending this.`,
		};
	}
	return ALLOWED;
}

export interface SendLimitDecision {
	allowed: boolean;
	limit: number;
	remaining: number;
	retryAfterSeconds: number | null;
	message?: string;
}

/**
 * Sending limits are an abuse and deliverability control. They are deliberately
 * independent of storage: buying more storage does not buy more sending.
 */
export function evaluateSendLimit(
	plan: PlanEntitlement,
	sentInLastHour: number,
	sentToday: number,
	overrideHourlyLimit?: number | null,
): SendLimitDecision {
	const hourlyLimit = overrideHourlyLimit ?? plan.hourlySendLimitPerMailbox;

	if (sentInLastHour >= hourlyLimit) {
		return {
			allowed: false,
			limit: hourlyLimit,
			remaining: 0,
			retryAfterSeconds: 3600,
			message:
				"This mailbox has reached its hourly sending limit. Sending resumes automatically within the hour.",
		};
	}

	const dailyLimit = plan.dailySendLimitPerMailbox;
	if (dailyLimit !== null && sentToday >= dailyLimit) {
		return {
			allowed: false,
			limit: dailyLimit,
			remaining: 0,
			retryAfterSeconds: 86400,
			message:
				"This mailbox has reached its daily sending limit. Contact support if you need a higher limit.",
		};
	}

	return {
		allowed: true,
		limit: hourlyLimit,
		remaining: hourlyLimit - sentInLastHour,
		retryAfterSeconds: null,
	};
}

const UNITS = ["B", "KB", "MB", "GB", "TB", "PB"];

export function formatBytes(bytes: number, fractionDigits?: number): string {
	if (!Number.isFinite(bytes) || bytes <= 0) return "0 GB";

	let value = bytes;
	let unit = 0;
	while (value >= 1024 && unit < UNITS.length - 1) {
		value /= 1024;
		unit += 1;
	}

	const digits = fractionDigits ?? (unit >= 3 && value < 100 ? 1 : 0);
	const rounded = value.toFixed(digits);
	// Prefer "25 GB" over "25.0 GB" for round numbers.
	const trimmed = rounded.endsWith(".0") ? rounded.slice(0, -2) : rounded;
	return `${trimmed} ${UNITS[unit]}`;
}

export function parseStorageToBytes(value: number, unit: "MB" | "GB" | "TB"): number {
	const multipliers = { MB: 1024 ** 2, GB: 1024 ** 3, TB: 1024 ** 4 };
	return Math.round(value * multipliers[unit]);
}
